import { createBrowserRouter, Navigate } from "react-router-dom";
import { AuthLayout } from "./layouts/AuthLayout";
import { DashboardLayout } from "./layouts/DashboardLayout";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { LoginPage } from "./pages/LoginPage";
import { EventListPage } from "./pages/events/EventListPage";
import { SessionListPage } from "./pages/sessions/SessionListPage";
import { EventLayout } from "./layouts/EventLayout";
import { PlaceholderPage } from "./pages/PlaceholderPage";
// DesignSystemPage is only referenced inside an `import.meta.env.DEV` branch
// below. In production, Vite replaces `import.meta.env.DEV` with `false`,
// Rollup eliminates the dead branch, sees the import is unreferenced, and
// drops both the import statement and the DesignSystemPage module from the
// bundle. Equivalent to `__DEV__`-gating in React Native.
import { DesignSystemPage } from "./pages/DesignSystemPage";
import { EventCreatePage } from "./pages/events/EventCreatePage";
import { EventEditPage } from "./pages/events/EventEditPage";
import { SessionCreatePage } from "./pages/sessions/SessionCreatePage";
import { SessionEditPage } from "./pages/sessions/SessionEditPage";
import { SponsorListPage } from "./pages/sponsors/SponsorListPage";
import { SponsorCreatePage } from "./pages/sponsors/SponsorCreatePage";
import { SponsorEditPage } from "./pages/sponsors/SponsorEditPage";
import { GroupsListPage } from "./pages/events/groups/GroupsListPage";
import { GroupCreatePage } from "./pages/events/groups/GroupCreatePage";
import { GroupEditPage } from "./pages/events/groups/GroupEditPage";
import { LocationsListPage } from "./pages/events/locations/LocationsListPage";
import { LocationCreatePage } from "./pages/events/locations/LocationCreatePage";
import { LocationEditPage } from "./pages/events/locations/LocationEditPage";
import { TravelListPage } from "./pages/events/travel/TravelListPage";
import { TravelCreatePage } from "./pages/events/travel/TravelCreatePage";
import { TravelEditPage } from "./pages/events/travel/TravelEditPage";
import { AttendeeListPage } from "./pages/attendees/AttendeeListPage";
import { AttendeeCreatePage } from "./pages/attendees/AttendeeCreatePage";
import { AttendeeReportPage } from "./pages/attendees/AttendeeReportPage";
import { NotificationListPage } from "./pages/notifications/NotificationListPage";
import { NotificationComposePage } from "./pages/notifications/NotificationComposePage";
import { NotificationDetailPage } from "./pages/notifications/NotificationDetailPage";
import { UserListPage } from "./pages/users/UserListPage";
import { UserCreatePage } from "./pages/users/UserCreatePage";
import { UserDetailPage } from "./pages/users/UserDetailPage";
import { AnalyticsPage } from "./pages/analytics/AnalyticsPage";
import { SurveyCreatePage } from "./pages/analytics/SurveyCreatePage";
import { SurveyDetailPage } from "./pages/analytics/SurveyDetailPage";
import { SupportPage } from "./pages/SupportPage";
import { PlanningWorkbookPage } from "./pages/PlanningWorkbookPage";

export const router = createBrowserRouter([
  {
    path: "/login",
    element: <AuthLayout />,
    children: [{ index: true, element: <LoginPage /> }],
  },
  {
    element: <ProtectedRoute />,
    children: [
      {
        element: <DashboardLayout />,
        children: [
          { path: "/", element: <Navigate to="/events" replace /> },
          { path: "/events", element: <EventListPage /> },
          { path: "/events/new", element: <EventCreatePage /> },
          { path: "/users", element: <UserListPage /> },
          { path: "/users/new", element: <UserCreatePage /> },
          { path: "/users/:personId", element: <UserDetailPage /> },
          { path: "/support", element: <SupportPage /> },
          ...(import.meta.env.DEV
            ? [{ path: "/design-system", element: <DesignSystemPage /> }]
            : []),
          {
            path: "/events/:eventId",
            element: <EventLayout />,
            children: [
              { index: true, element: <EventEditPage /> },
              { path: "agenda", element: <SessionListPage /> },
              { path: "agenda/new", element: <SessionCreatePage /> },
              { path: "agenda/:sessionId", element: <SessionEditPage /> },
              { path: "attendees", element: <AttendeeListPage /> },
              { path: "attendees/new", element: <AttendeeCreatePage /> },
              { path: "attendees/:personId/report", element: <AttendeeReportPage /> },
              { path: "sponsors", element: <SponsorListPage /> },
              { path: "sponsors/new", element: <SponsorCreatePage /> },
              { path: "sponsors/:sponsorId", element: <SponsorEditPage /> },
              { path: "groups", element: <GroupsListPage /> },
              { path: "groups/new", element: <GroupCreatePage /> },
              { path: "groups/:groupId", element: <GroupEditPage /> },
              { path: "locations", element: <LocationsListPage /> },
              { path: "locations/new", element: <LocationCreatePage /> },
              { path: "locations/:locationId", element: <LocationEditPage /> },
              { path: "travel", element: <TravelListPage /> },
              { path: "travel/new", element: <TravelCreatePage /> },
              { path: "travel/:travelItemId", element: <TravelEditPage /> },
              { path: "notifications", element: <NotificationListPage /> },
              { path: "notifications/new", element: <NotificationComposePage /> },
              { path: "notifications/:notificationId", element: <NotificationDetailPage /> },
              // Surveys and Check-in are sub-views of Analytics, not standalone
              // sidebar destinations — reached via in-page navigation. ADR-054.
              { path: "analytics", element: <AnalyticsPage /> },
              { path: "analytics/surveys/new", element: <SurveyCreatePage /> },
              { path: "analytics/surveys/:surveyId", element: <SurveyDetailPage /> },
              { path: "planning-workbook", element: <PlanningWorkbookPage /> },
              { path: "analytics/checkin", element: <PlaceholderPage title="Check-in" /> },
            ],
          },
        ],
      },
    ],
  },
  { path: "*", element: <Navigate to="/events" replace /> },
]);
